import {
  array,
  combineLatestTyped,
  tuple,
  useDataStream,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo, useEffect } from 'react';
import { map, switchMap } from 'rxjs/operators';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../global-state/database/common/list-with-status';
import { companyListOf } from '../../global-state/database/company-list';
import {
  fetchKoumutenList,
  koumutenList$
} from '../../global-state/database/koumuten-list';
import { modelImageListOf } from '../../global-state/database/model-image-list';
import { modelListOf } from '../../global-state/database/model-list';
import { projectImageListOf } from '../../global-state/database/project-image-list';
import { projectListOf } from '../../global-state/database/project-list';
import {
  fetchUserList,
  userList$
} from '../../global-state/database/user-list';
import {
  fetchCsvList,
  csvList$
} from '../../global-state/database/csv-list';
import {
  companyId$,
  koumutenId$,
  modelId$,
  projectId$
} from '../../global-state/router-id';
import { pathList$ } from '../../global-state/router-pathname';
import { ICompanyType } from '../../types/record/company';
import {
  IImageInfoType,
  IProjectImageInfoType
} from '../../types/record/image-info';
import { IModelType } from '../../types/record/model';
import { IProjectType } from '../../types/record/project';
import { BreadcrumbsJpView } from './breadcrumbs-view';
import { getPathDisplayNameList } from './get-path-display-name';

interface Props {
  showPadding?: boolean;
}

export const BreadcrumbsJp: FC<Props> = memo(
  ({ showPadding = false }: Readonly<Props>) => {
    const companyList$ = useDataStream<ListWithPromiseState<ICompanyType>>(
      listWithPromiseStateInit<ICompanyType>(),
      koumutenId$.pipe(switchMap(companyListOf))
    );

    const modelList$ = useDataStream<ListWithPromiseState<IModelType>>(
      listWithPromiseStateInit<IModelType>(),
      companyId$.pipe(switchMap(modelListOf))
    );

    const projectList$ = useDataStream<ListWithPromiseState<IProjectType>>(
      listWithPromiseStateInit<IProjectType>(),
      companyId$.pipe(switchMap(projectListOf))
    );

    const modelImageList$ = useDataStream<ListWithPromiseState<IImageInfoType>>(
      listWithPromiseStateInit<IImageInfoType>(),
      modelId$.pipe(switchMap(modelImageListOf))
    );

    const projectImageList$ = useDataStream<
      ListWithPromiseState<IProjectImageInfoType>
    >(
      listWithPromiseStateInit<IProjectImageInfoType>(),
      projectId$.pipe(switchMap(projectImageListOf))
    );

    useEffect(() => {
      fetchUserList();
      fetchKoumutenList();
      fetchCsvList();
    }, []);

    const pathDisplayNameElemList$ = useDataStream<string[]>(
      [],
      combineLatestTyped(
        pathList$,
        userList$,
        csvList$,
        koumutenList$,
        companyList$,
        modelList$,
        projectList$,
        modelImageList$,
        projectImageList$
      ).pipe(map(values => getPathDisplayNameList(...values)))
    );

    const pathnameLast$ = useDataStream<string>(
      '',
      pathDisplayNameElemList$.pipe(map(list => array.last(list) ?? ''))
    );

    const links$ = useDataStream<[string, string][]>(
      [],
      combineLatestTyped(pathList$, pathDisplayNameElemList$).pipe(
        map(([pathnameElmList, pathDisplayNameElemList]) =>
          array
            .skipLast(pathDisplayNameElemList, 1)
            .map((pathDisplayNameElement, idx) =>
              tuple(
                pathDisplayNameElement,
                `/${array.take(pathnameElmList, idx + 1).join('/')}`
              )
            )
        )
      )
    );

    /* extract values */

    const pathnameLast = useStreamValue(pathnameLast$, '');
    const links = useStreamValue(links$, []);

    return (
      <BreadcrumbsJpView
        pathLast={pathnameLast}
        links={links}
        showPadding={showPadding}
      />
    );
  }
);

BreadcrumbsJp.displayName = 'BreadcrumbsJp';
